// React and React related
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Third party libraries
import { Form, Input, Row, Col, Card, Upload, notification, Select } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

// Constants and types
import {
  Environment,
  IMAGE_TYPE_WITHOUT_PDF,
  isDisableBeforeCurrentDate,
  MAX_PROFILE_PICTURE_COUNT,
} from 'configs/constants';
import { MAX_FILE_SIZE_MB } from 'configs';
import { ErrorMessage, getErrorMessage } from 'utils/axios';
import { NotificationProps } from 'utils/types';
import { AgencyProfileFormValues, AgencyProfileDTO, LogoChangeInfo } from './types';

// Utils
import { customizeRequiredMark, fieldDefaultProps } from 'utils/fieldConfig';
import { transformFile } from 'utils/file';
import { validateUploadSize } from 'utils/validateUploadSize';

// Components
import Button from 'app/atoms/Button';
import CountryInput from 'app/atoms/CountryInput';
import CustomDatePicker from 'app/atoms/CustomDatePicker';
import FormSkeleton from 'app/atoms/FormSkeleton';
import GooglePlacesInput from 'app/atoms/GooglePlacesAutocomplete';
import LanguagePreferrence from 'app/atoms/LanguagePreferrence';
import Modal from 'app/atoms/Modal';
import PaymentCard from 'app/molecules/PaymentCard/PaymentCard';
import GlobalSearchBar from 'app/atoms/GlobalSearchBar';
import ConfirmationModal from 'app/molecules/ConfirmationModal';

// Services and hooks
import { getAgencyProfile, updateAgencyProfile, deleteAgency, IAgencyProfileResponse } from './services';
import { useUserInfo, USER_INFO_QUERY_KEY } from 'app/hooks/useUserInfo';
import { useCountries } from 'app/hooks/useCountries';
import PaymentMethodForm from './PaymentMethodForm';

// Assets
import CompanyLogoIcon from 'assets/icons/CompanyLogo';
import { auth } from 'app/modules/Auth/firebaseConfig';
import flagImages from 'utils/flagImages';
import { Authorize } from 'app/atoms';
import { PERMISSIONS_SUBJECTS } from '../Auth/constants';
import { PermissionActions } from '../Auth/types';

const AgencyProfile: React.FC = () => {
  const [form] = Form.useForm<AgencyProfileFormValues>();
  const [paymentForm] = Form.useForm();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [hasChanges, setHasChanges] = useState(false);

  const { data: currentUser } = useUserInfo();
  const { data: countriesData } = useCountries();
  const agencyId = String(currentUser?.agency?.id ?? '');
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [isPaymentModalVisible, setIsPaymentModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);

  // Fetch agency profile data
  const {
    data: agencyProfile,
    isLoading,
    isFetching,
  } = useQuery<IAgencyProfileResponse>({
    queryKey: ['agency-profile', agencyId],
    queryFn: () => getAgencyProfile(agencyId),
    enabled: Boolean(agencyId),
  });

  // Update agency profile mutation
  const updateProfileMutation = useMutation({
    mutationKey: ['update-agency-profile'],
    mutationFn: (formData: FormData) => updateAgencyProfile(agencyId, formData),
    onSuccess: (_response) => {
      queryClient.invalidateQueries({ queryKey: ['agency-profile'] });
      queryClient.invalidateQueries({ queryKey: USER_INFO_QUERY_KEY });
      notification.success({
        message: t('profile.form.updateSuccess'),
        ...NotificationProps,
      });
      setHasChanges(false);
    },
    onError: (error: ErrorMessage) => {
      notification.error({
        message: getErrorMessage(error),
        ...NotificationProps,
      });
    },
  });

  // Delete agency mutation
  const deleteAgencyMutation = useMutation({
    mutationKey: ['delete-agency'],
    mutationFn: () => deleteAgency(agencyId),
    onSuccess: async () => {
      notification.success({
        message: t('apiSuccessMessages.deleteAgency'),
        ...NotificationProps,
      });
      setIsDeleteModalVisible(false);
      localStorage.clear();
      await auth.signOut();
    },
    onError: (error: ErrorMessage) => {
      notification.error({
        message: getErrorMessage(error),
        ...NotificationProps,
      });
    },
  });

  // Set form values when data is loaded
  useEffect(() => {
    if (agencyProfile && !isFetching) {
      const selectedCountry = countriesData?.data?.find(
        (country) =>
          country.currencyCode === agencyProfile?.currencyCode && country.isoAlpha2 === agencyProfile?.currencyCodeIso,
      );

      const formValues: AgencyProfileFormValues = {
        countryId: agencyProfile.countryId ?? 0,
        registrationNumber: agencyProfile.registrationNumber,
        address: agencyProfile.address,
        preferredLanguage: agencyProfile.preferredLanguage,
        agencyName: agencyProfile.agencyName,
        tradeName: agencyProfile.tradeName,
        agencyWebsite: agencyProfile.agencyWebsite,
        licenseNumber: agencyProfile.license.licenseNumber,
        licenseType: agencyProfile.license.licenseType,
        licenseExpiryDate: agencyProfile.license.licenseExpiryDate
          ? dayjs(agencyProfile.license.licenseExpiryDate)
          : null,
        currencyCode:
          agencyProfile.currencyCode && agencyProfile.currencyCodeIso
            ? agencyProfile.currencyCode + ' ' + agencyProfile.currencyCodeIso + ' ' + selectedCountry?.name
            : null,
      };

      form.setFieldsValue(formValues);
      setLogoPreview(agencyProfile.agencyLogo || null);
    }
  }, [agencyProfile, form, isFetching]);

  const handleCurrencyChange = (value: string) => {
    form.setFieldsValue({ currencyCode: value });
  };

  const checkForChanges = (changedValues: Partial<AgencyProfileFormValues>, allValues: AgencyProfileFormValues) => {
    if (!agencyProfile) return;

    // Only check the fields that have actually changed
    const changedKeys = Object.keys(changedValues);
    if (changedKeys.length === 0) {
      setHasChanges(false);
      return;
    }

    const hasFormChanges = changedKeys.some((key) => {
      const currentValue = allValues[key as keyof AgencyProfileFormValues];

      // Special handling for different field types
      switch (key) {
        case 'countryId':
          return currentValue !== (agencyProfile.countryId ?? 0);
        case 'licenseExpiryDate': {
          const currentDate = currentValue as string;
          const originalDate = agencyProfile.license.licenseExpiryDate;
          return currentDate !== originalDate;
        }
        case 'licenseNumber':
        case 'licenseType':
          return currentValue !== agencyProfile.license[key === 'licenseNumber' ? 'licenseNumber' : 'licenseType'];
        case 'profilePicture':
          // Check if a new file has been uploaded
          return (
            currentValue && Array.isArray(currentValue) && currentValue.length > 0 && currentValue[0]?.originFileObj
          );
        case 'agencyName':
        case 'tradeName':
        case 'agencyWebsite':
        case 'registrationNumber':
        case 'address':
        case 'preferredLanguage':
        case 'currencyCode':
          return currentValue !== agencyProfile[key as keyof typeof agencyProfile];
        default:
          return false;
      }
    });

    setHasChanges(hasFormChanges);
  };

  const handleSubmit = (values: AgencyProfileFormValues) => {
    const formData = new FormData();

    // Create a DTO object with all the form values
    const agencyDTO: AgencyProfileDTO = {
      agentId: agencyId,
      agencyName: values.agencyName,
      tradeName: values.tradeName,
      agencyWebsite: values.agencyWebsite === undefined ? null : values.agencyWebsite?.trim() || null,
      license: {
        licenseNumber: values.licenseNumber,
        licenseType: values.licenseType === undefined ? null : values.licenseType?.trim() || null,
        licenseExpiryDate: values.licenseExpiryDate?.format('YYYY-MM-DD') ?? '',
      },
      countryId: values.countryId,
      registrationNumber: values.registrationNumber,
      address: values.address,
      preferredLanguage: values.preferredLanguage,
      currencyCode: values.currencyCode?.split(' ')[0],
      currencyCodeIso: values.currencyCode?.split(' ')[1],
    };

    // Append the stringified DTO to the FormData
    formData.append('agencyDTO', JSON.stringify(agencyDTO));

    // Add profile picture if uploaded
    if (values.profilePicture && values.profilePicture.length > 0) {
      const file = values.profilePicture[0]?.originFileObj;
      if (file && file instanceof Blob) {
        const transformedFile = transformFile(file);
        formData.append('logo', transformedFile);
      }
    }

    updateProfileMutation.mutate(formData);
  };

  const handleLogoChange = (info: LogoChangeInfo) => {
    if (info.fileList && info.fileList.length > 0) {
      const file = info.fileList[0];
      if (file.originFileObj) {
        const imageUrl = URL.createObjectURL(file.originFileObj);
        setLogoPreview(imageUrl);
        setHasChanges(true);
        // Update the form field value
        form.setFieldsValue({ profilePicture: info.fileList });
      }
    } else {
      // Clear the form field if no file is selected
      form.setFieldsValue({ profilePicture: undefined });
    }
  };

  const handleAddPaymentMethod = () => {
    setIsPaymentModalVisible(true);
  };

  const handlePaymentModalClose = () => {
    setIsPaymentModalVisible(false);
    paymentForm.resetFields();
  };

  const handlePaymentSubmit = async () => {
    try {
      await paymentForm.validateFields();
      // TODO: Implement payment method addition logic
      notification.success({
        message: t('profile.paymentMethods.addSuccess'),
        ...NotificationProps,
      });
      setIsPaymentModalVisible(false);
      paymentForm.resetFields();
    } catch (error) {
      notification.error({
        message: getErrorMessage(error as ErrorMessage),
        ...NotificationProps,
      });
    }
  };

  const handleDeleteAgency = () => {
    setIsDeleteModalVisible(true);
  };

  const handleDeleteModalClose = () => {
    // Prevent closing modal while deletion is in progress
    if (deleteAgencyMutation.isPending) {
      return;
    }
    setIsDeleteModalVisible(false);
  };

  const handleConfirmDelete = () => {
    deleteAgencyMutation.mutate();
  };

  return (
    <>
      <GlobalSearchBar />
      <Card className="mb-6">
        {isLoading || isFetching ? (
          <FormSkeleton rows={4} cols={1} />
        ) : (
          <Form
            form={form}
            requiredMark={customizeRequiredMark}
            layout="vertical"
            onFinish={handleSubmit}
            onValuesChange={checkForChanges}
            data-testid="agency-profile-form"
          >
            {/* Logo */}
            <Row gutter={[24, 0]}>
              <Col xs={24}>
                <h2 className="text-2xl font-bold mb-6 ltr:text-left rtl:text-right">{t('profile.form.logo.title')}</h2>
                <div className="w-full flex justify-center mb-2">
                  <div className="w-full border border-borderColor-primary rounded-lg p-4">
                    <div className="w-48 aspect-square mx-auto">
                      {logoPreview ? (
                        <img
                          src={logoPreview}
                          alt={t('profile.form.logo.photoAlt')}
                          className="w-full h-full object-contain rounded-2xl"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-white rounded-2xl border border-neutral-800">
                          <CompanyLogoIcon />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex mb-4 justify-end">
                  <Authorize
                    requiredPermissions={[
                      { action: PermissionActions.EDIT_AGENCY_DETAILS, subject: PERMISSIONS_SUBJECTS.AGENCY },
                    ]}
                  >
                    <Form.Item
                      className="!mb-0.5 w-full flex justify-end"
                      name="profilePicture"
                      extra={
                        <span className="text-gray-600 text-sm italic">
                          {t('profile.form.photoFormat', { maxSize: MAX_FILE_SIZE_MB })}
                        </span>
                      }
                    >
                      <div className="w-full flex justify-end">
                        <Upload
                          className="upload-custom-width"
                          beforeUpload={(file) => validateUploadSize(file, t, true, 'images')}
                          maxCount={MAX_PROFILE_PICTURE_COUNT}
                          accept={IMAGE_TYPE_WITHOUT_PDF}
                          data-testid="profile-picture-upload"
                          showUploadList={false}
                          onChange={handleLogoChange}
                        >
                          <Button
                            {...fieldDefaultProps}
                            variant="outlined"
                            color="primary"
                            className="body-semibold"
                            data-testid="change-photo-button"
                          >
                            <span className="h5-semibold">{t('profile.form.logo.uploadButton')}</span>
                          </Button>
                        </Upload>
                      </div>
                    </Form.Item>
                  </Authorize>
                </div>
              </Col>
            </Row>
            {/* Language Preference */}
            <Row gutter={[24, 0]}>
              <Col xs={24} lg={24}>
                <h2 className="text-2xl font-bold mb-6 ltr:text-left rtl:text-right">
                  {t('profile.form.languagePreference.title')}
                </h2>
                <LanguagePreferrence />
              </Col>
            </Row>
            {/* Agency Info */}
            <h2 className="text-2xl font-bold mb-6 ltr:text-left rtl:text-right">
              {t('profile.form.agencyInfo.title')}
            </h2>
            <Row gutter={[24, 0]}>
              <Col xs={24} md={12}>
                <Form.Item
                  label={t('profile.form.agencyInfo.fields.agencyName')}
                  name="agencyName"
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: t('profile.form.agencyInfo.required.agencyName'),
                    },
                    { min: 2, message: t('profile.form.agencyInfo.rules.agencyName') },
                    { max: 100, message: t('profile.form.agencyInfo.rules.agencyName') },
                  ]}
                  validateTrigger="onBlur"
                >
                  <Input
                    {...fieldDefaultProps}
                    placeholder={t('profile.form.agencyInfo.placeholder.agencyName')}
                    data-testid="company-name-input"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label={t('profile.form.agencyInfo.fields.agencyTradeName')}
                  name="tradeName"
                  rules={[
                    { required: true, message: t('profile.form.agencyInfo.required.agencyTradeName') },
                    { min: 2, message: t('profile.form.agencyInfo.rules.minAgencyTradeName') },
                    { max: 100, message: t('profile.form.agencyInfo.rules.maxAgencyTradeName') },
                  ]}
                  validateTrigger="onBlur"
                >
                  <Input
                    {...fieldDefaultProps}
                    placeholder={t('profile.form.agencyInfo.placeholder.agencyTradeName')}
                    data-testid="trade-name-input"
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[24, 0]}>
              <Col xs={24} md={12}>
                <Form.Item
                  label={t('profile.form.agencyInfo.fields.agencyRegistrationNumber')}
                  name="registrationNumber"
                  rules={[{ required: true, message: t('profile.form.agencyInfo.required.agencyRegistrationNumber') }]}
                >
                  <Input
                    {...fieldDefaultProps}
                    placeholder={t('profile.form.agencyInfo.placeholder.agencyRegistrationNumber')}
                    data-testid="registration-number-input"
                    disabled={true}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label={t('profile.form.agencyInfo.fields.agencyWebsite')} name="agencyWebsite">
                  <Input
                    {...fieldDefaultProps}
                    placeholder={t('profile.form.agencyInfo.placeholder.agencyWebsite')}
                    data-testid="website-input"
                  />
                </Form.Item>
              </Col>
            </Row>
            {/* Agency Address */}
            <h2 className="text-2xl font-bold mb-6 ltr:text-left rtl:text-right">
              {t('profile.form.agencyAddress.title')}
            </h2>
            <Row gutter={[24, 0]}>
              <Col xs={24} md={12}>
                <CountryInput
                  form={form}
                  name="countryId"
                  label={t('profile.form.agencyAddress.fields.country')}
                  dataTestId="country-select"
                  requiredMessage={t('traveler.form.rules.nationality')}
                  disabled={true}
                />
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label={t('auth.signup.currency')}
                  name="currencyCode"
                  rules={[
                    {
                      required: !agencyProfile?.isTripExist,
                      message: t('auth.signup.rules.currency_required'),
                    },
                  ]}
                  validateTrigger="onChange"
                >
                  <Select
                    {...fieldDefaultProps}
                    placeholder={t('auth.signup.rules.currency_required')}
                    data-testid="currency-select"
                    onChange={handleCurrencyChange}
                    showSearch
                    allowClear
                    disabled={agencyProfile?.isTripExist}
                  >
                    {countriesData?.data?.map((country) => (
                      <Select.Option
                        key={country.id}
                        value={country.currencyCode + ' ' + country.isoAlpha2 + ' ' + country.name}
                        data-testid={`currency-option-${country.currencyCode}`}
                      >
                        <span className="flex items-center gap-2">
                          <img
                            src={flagImages[country?.regionFlag?.replace?.('.png', '')]}
                            alt={country.currencyCode}
                            className="w-4 h-3"
                          />
                          {country.currencyCode} - {country.currencyName} ({country.name})
                        </span>
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[24, 0]}>
              <Col xs={24} md={12}>
                <Form.Item
                  label={t('profile.form.agencyAddress.fields.address')}
                  name="address"
                  rules={[
                    { required: true, message: t('profile.form.agencyAddress.required.address') },
                    { min: 3, message: t('profile.form.agencyAddress.rules.minAddress') },
                  ]}
                >
                  <GooglePlacesInput
                    onPlaceSelected={(place) => {
                      place?.formatted_address && form.setFieldsValue({ address: place.formatted_address });
                    }}
                    placeholder={t('profile.form.addressPlaceholder')}
                    dataTestId="company-address-input"
                    placeType="address"
                    value={form.getFieldValue('address')}
                    onChange={(value) => {
                      form.setFieldsValue({ address: value });
                    }}
                    onBlur={() => {
                      form.validateFields(['address']);
                    }}
                    form={form}
                    fieldName="address"
                    validationMessage={t('profile.form.agencyAddress.required.address')}
                  />
                </Form.Item>
              </Col>
            </Row>
            {/* Agency License */}
            <h2 className="text-2xl font-bold mb-6 ltr:text-left rtl:text-right">
              {t('profile.form.agencyLicense.title')}
            </h2>
            <Row gutter={[24, 0]}>
              <Col xs={24} md={12}>
                <Form.Item
                  label={t('profile.form.agencyLicense.fields.licenseNumber')}
                  name="licenseNumber"
                  rules={[{ required: false, message: t('profile.form.agencyLicense.required.licenseNumber') }]}
                >
                  <Input
                    {...fieldDefaultProps}
                    placeholder={t('profile.form.agencyLicense.placeholder.licenseNumber')}
                    data-testid="license-number-input"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label={t('profile.form.agencyLicense.fields.licenseType')} name="licenseType">
                  <Input
                    {...fieldDefaultProps}
                    placeholder={t('profile.form.agencyLicense.placeholder.licenseType')}
                    data-testid="license-type-input"
                  />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  label={t('profile.form.agencyLicense.fields.licenseExpiryDate')}
                  name="licenseExpiryDate"
                  rules={[{ required: false, message: t('profile.form.agencyLicense.required.licenseExpiryDate') }]}
                >
                  <CustomDatePicker
                    {...fieldDefaultProps}
                    className="w-full"
                    placeholder={t('profile.form.agencyLicense.placeholder.licenseExpiryDate')}
                    data-testid="license-expiration-input"
                    disabledDate={isDisableBeforeCurrentDate}
                    useCustomPopupContainer
                    useCustomPopupStyle
                  />
                </Form.Item>
              </Col>
            </Row>
            <Authorize
              requiredPermissions={[
                { action: PermissionActions.EDIT_AGENCY_DETAILS, subject: PERMISSIONS_SUBJECTS.AGENCY },
              ]}
            >
              <div className="flex justify-end">
                <Button
                  {...fieldDefaultProps}
                  type="primary"
                  size="large"
                  htmlType="submit"
                  data-testid="save-changes-button"
                  loading={updateProfileMutation.isPending}
                  disabled={!hasChanges}
                  className=""
                >
                  {t('profile.form.saveChanges')}
                </Button>
              </div>
            </Authorize>
          </Form>
        )}
      </Card>

      {(import.meta.env.VITE_APP_MODE === Environment.LOCAL || import.meta.env.VITE_APP_MODE === Environment.DEV) && (
        <>
          <Card className="mb-6">
            <h2 className="text-2xl font-bold mb-6 ltr:text-left rtl:text-right">
              {t('profile.subscriptionPlan.title')}
            </h2>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="flex flex-col">
                <span className="text-lg text-primary-600 font-semibold">{t('profile.subscriptionPlan.plan')}</span>
                <span className="text-sm text-gray-600">{t('profile.subscriptionPlan.nextBillingDate')}</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 w-full sm:w-auto">
                <Authorize
                  requiredPermissions={[
                    { action: PermissionActions.EDIT_AGENCY_DETAILS, subject: PERMISSIONS_SUBJECTS.AGENCY },
                  ]}
                >
                  <Button {...fieldDefaultProps} variant="outlined" color="primary" className="w-full sm:w-auto">
                    <span className="h5-semibold">{t('profile.subscriptionPlan.cancelSubscription')}</span>
                  </Button>
                  <Button {...fieldDefaultProps} variant="solid" color="primary" className="w-full sm:w-auto">
                    <span className="h5-semibold">{t('profile.subscriptionPlan.upgradePlan')}</span>
                  </Button>
                </Authorize>
              </div>
            </div>
          </Card>
          <Card className="mb-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
              <h2 className="text-2xl font-bold ltr:text-left rtl:text-right">{t('profile.paymentMethods.title')}</h2>
              <Authorize
                requiredPermissions={[
                  { action: PermissionActions.EDIT_AGENCY_DETAILS, subject: PERMISSIONS_SUBJECTS.AGENCY },
                ]}
              >
                <Button
                  {...fieldDefaultProps}
                  icon={<PlusOutlined />}
                  variant="outlined"
                  color="primary"
                  data-testid="add-payment-method-button"
                  onClick={handleAddPaymentMethod}
                  className="w-full sm:w-auto"
                >
                  <span className="h5-semibold">{t('profile.paymentMethods.addNew')}</span>
                </Button>
              </Authorize>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <PaymentCard
                data={{
                  cardHolderName: 'John Doe',
                  expiryDate: '12/24',
                  cardNumber: '4242 4242 4242 4242',
                  cardType: 'visa',
                }}
                onEdit={() => {}}
              />
            </div>
          </Card>
        </>
      )}

      {/* Delete Account Section */}
      <Authorize
        requiredPermissions={[{ action: PermissionActions.EDIT_AGENCY_DETAILS, subject: PERMISSIONS_SUBJECTS.AGENCY }]}
      >
        <Card>
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold ltr:text-left rtl:text-right mb-0">{t('profile.delete.title')}</h2>
            <Button
              {...fieldDefaultProps}
              variant="outlined"
              color="danger"
              size="large"
              data-testid="delete-agency-button"
              onClick={handleDeleteAgency}
              loading={deleteAgencyMutation.isPending}
              disabled={deleteAgencyMutation.isPending}
            >
              {t('profile.delete.button')}
            </Button>
          </div>
        </Card>
      </Authorize>

      <Modal
        isOpen={isPaymentModalVisible}
        title={t('profile.paymentMethods.form.title')}
        className="!w-[800px]"
        onClose={handlePaymentModalClose}
        onSubmit={handlePaymentSubmit}
        submitText={t('profile.paymentMethods.form.saveButton')}
        cancelText={t('profile.paymentMethods.form.cancelButton')}
        onCancel={handlePaymentModalClose}
        dataTestid="add-payment-modal"
        destroyOnClose
      >
        <PaymentMethodForm form={paymentForm} onFinish={handlePaymentSubmit} />
      </Modal>

      {/* Delete Account Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteModalVisible}
        onClose={handleDeleteModalClose}
        onConfirm={handleConfirmDelete}
        title={t('profile.delete.modal.message')}
        description={deleteAgencyMutation.isPending ? t('profile.delete.modal.deleting') : undefined}
        confirmText={t('profile.delete.modal.confirm')}
        cancelText={t('profile.delete.modal.cancel')}
        centered
        dangerConfirm
        loading={deleteAgencyMutation.isPending}
      />
    </>
  );
};

export default AgencyProfile;
